import {useComputedColorScheme} from '@mantine/core'
import {AdvancedMarker, Map, Polyline, useMap} from '@vis.gl/react-google-maps'
import {useEffect, useMemo, useRef} from 'react'

import {MapLoadObserver, type MapLoadTracking} from 'src/jobs/google-maps/map-load-tracking'

import classes from './run-map.module.css'
import {LATE_LEG_COLOR, ROUTE_COLOR} from './run-map-colors'
import type {RunMapSurfaceProps} from './run-map-surface'

const RUN_MAP_ID = 'dispatch-run-map'

type MapBounds = google.maps.LatLngBoundsLiteral & {padding: number}

/** Fit the map to `bounds` once per `fitKey`. A null key skips fitting. */
function FitBounds({bounds, fitKey}: {bounds: MapBounds | null; fitKey: string | null}) {
    const map = useMap()
    const fittedKey = useRef<string | null>(null)

    useEffect(() => {
        if (!map || !bounds || fitKey == null || fittedKey.current === fitKey) {
            return
        }

        fittedKey.current = fitKey
        const {padding, ...box} = bounds
        map.fitBounds(box, padding)
    }, [bounds, fitKey, map])

    return null
}

export function RunMapGoogle({
    pins,
    yardMarkers,
    legs,
    onOpenJob,
    fitKey,
    tracking,
}: RunMapSurfaceProps & {fitKey: string | null; tracking: MapLoadTracking}) {
    const colorScheme = useComputedColorScheme('light')
    const bounds = useMemo((): MapBounds | null => {
        const points = [
            ...pins.map((pin) => pin.position),
            ...yardMarkers.map((marker) => marker.position),
        ]
        if (points.length === 0) {
            return null
        }

        const lats = points.map((point) => point.lat)
        const lngs = points.map((point) => point.lng)
        const pad = 0.02
        return {
            north: Math.max(...lats) + pad,
            south: Math.min(...lats) - pad,
            east: Math.max(...lngs) + pad,
            west: Math.min(...lngs) - pad,
            padding: 40,
        }
    }, [pins, yardMarkers])

    return (
        <Map
            reuseMaps
            mapId={RUN_MAP_ID}
            className={classes['googleMap']}
            defaultBounds={bounds ?? undefined}
            defaultCenter={bounds ? undefined : {lat: 29.9, lng: -94.8}}
            defaultZoom={bounds ? undefined : 9}
            gestureHandling="cooperative"
            colorScheme={colorScheme === 'dark' ? 'DARK' : 'LIGHT'}
            mapTypeControl={false}
            streetViewControl={false}
            clickableIcons={false}
            onTilesLoaded={tracking.markReady}
        >
            <MapLoadObserver tracking={tracking} />
            <FitBounds bounds={bounds} fitKey={fitKey} />
            {legs.map((leg) => (
                <Polyline
                    key={`${leg.key}:${leg.toStop?.number ?? 'yard'}`}
                    path={[leg.from, leg.to]}
                    strokeColor={leg.lateByMinutes != null ? LATE_LEG_COLOR.hex : ROUTE_COLOR.hex}
                    strokeOpacity={0.9}
                    strokeWeight={leg.lateByMinutes != null ? 5 : 4}
                />
            ))}
            {yardMarkers.map((marker) => (
                <AdvancedMarker
                    key={marker.yard.id}
                    position={marker.position}
                    title={`${marker.yard.name} yard`}
                    zIndex={1}
                >
                    <div
                        className={classes['googleYard']}
                        style={{background: marker.color.css}}
                        aria-hidden
                    >
                        ⌂
                    </div>
                </AdvancedMarker>
            ))}
            {pins.map((pin) => {
                const filled = pin.job.staffing === 'staffed'
                return (
                    <AdvancedMarker
                        key={pin.job.id}
                        position={pin.position}
                        title={pin.ariaLabel}
                        zIndex={pin.stopNumber != null ? 3 : 2}
                        onClick={() => onOpenJob(pin.job.id)}
                    >
                        <div
                            className={classes['googlePin']}
                            style={{
                                borderColor: pin.color.css,
                                background: filled ? pin.color.css : 'var(--mantine-color-white)',
                                color: filled
                                    ? 'var(--mantine-color-white)'
                                    : 'var(--mantine-color-gray-9)',
                                opacity: pin.dimmed ? 0.45 : 1,
                            }}
                        >
                            {pin.stopNumber ?? ''}
                        </div>
                    </AdvancedMarker>
                )
            })}
        </Map>
    )
}
